import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from 'axios';

function SuccessPagePayment() {
    const VERIFY_API_ENDPOINT = "https://www.bharatcod.com/admin/payment/verify-status";

    const [verificationStatus, setVerificationStatus] = useState("verifying");
    const [isLoading, setIsLoading] = useState(true);
    const [orderId, setOrderId] = useState(null);
    const [message, setMessage] = useState("We are securely checking your payment status...");

    const isSuccess = verificationStatus === "success";

    useEffect(() => {
        const verifyPayment = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const txnId = urlParams.get('txnId');

            if (!txnId) {
                setVerificationStatus("failed");
                setMessage("Error: Transaction ID missing in URL. Payment verification failed.");
                setIsLoading(false);
                return;
            }

            try {
                const response = await axios.post(VERIFY_API_ENDPOINT, {
                    merchantTransactionId: txnId
                });

                const data = response.data;

                if (data.paymentStatus === 'Success') {
                    setVerificationStatus("success");
                    setOrderId(data.orderID);
                    setMessage(`Payment successful for Order ID: ${data.orderID}.`);
                } else {
                    setVerificationStatus("failed");
                    setOrderId(data.orderID || null);
                    setMessage(`Payment failed or is still pending. Reason: ${data.msg || 'Transaction not successful.'}`);
                }

            } catch (error) {
                setVerificationStatus("failed");
                setMessage("Verification failed due to a server error. Please check your order history.");
            } finally {
                setIsLoading(false);
            }
        };

        verifyPayment();
    }, []);

    const handleRedirect = () => {
        if (isSuccess && orderId) {
            window.location.assign(`/ThankYouPage?orderId=${orderId}`);
        } else {
            window.location.assign("/");
        }
    };


    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p className="text-xl text-gray-700">Checking payment status... Please do not close this window.</p>
            </div>
        );
    }
    return (
        <div className="min-h-screen flex items-center justify-center bg-[#f7f8fc] relative overflow-hidden">
            <div className="absolute w-80 h-80 bg-emerald-200 rounded-full blur-3xl opacity-30 top-10 left-10"></div>
            <div className="absolute w-96 h-96 bg-purple-200 rounded-full blur-3xl opacity-30 bottom-10 right-10"></div>

            <motion.div
                initial={{ opacity: 0, y: 40, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="relative z-10 w-[90%] sm:w-[420px] bg-white/80 backdrop-blur-xl rounded-3xl shadow-[0_8px_40px_rgba(0,0,0,0.05)] border border-gray-100 p-10 overflow-hidden"
            >
                <motion.div
                    animate={{
                        boxShadow: isSuccess
                            ? [
                                "0 0 0 rgba(0,0,0,0)",
                                "0 0 25px rgba(34,197,94,0.3)",
                                "0 0 0 rgba(0,0,0,0)",
                            ]
                            : [
                                "0 0 0 rgba(0,0,0,0)",
                                "0 0 25px rgba(239,68,68,0.3)",
                                "0 0 0 rgba(0,0,0,0)",
                            ],
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="absolute inset-0 rounded-3xl"
                />

                <motion.div
                    className="flex justify-center mb-8"
                    animate={
                        isSuccess ? { scale: [1, 1.1, 1] } : { x: [0, -10, 10, -10, 10, 0] } // shake on fail
                    }
                    transition={{ duration: 0.6 }}
                >
                    <div
                        className={`relative flex items-center justify-center w-24 h-24 rounded-full ${isSuccess
                            ? "bg-gradient-to-br from-green-400 to-emerald-600"
                            : "bg-gradient-to-br from-red-400 to-rose-600"
                            } shadow-lg`}
                    >
                        <div className="absolute inset-0 rounded-full bg-white opacity-20 blur-sm"></div>

                        <AnimatePresence>
                            {isSuccess ? (
                                <>
                                    <motion.svg
                                        key="check"
                                        className="w-14 h-14 text-white"
                                        viewBox="0 0 52 52"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        exit={{ scale: 0 }}
                                        transition={{ type: "spring", stiffness: 120, damping: 10 }}
                                    >
                                        <motion.path
                                            d="M14 27 L22 35 L38 17"
                                            initial={{ pathLength: 0 }}
                                            animate={{ pathLength: 1 }}
                                            transition={{ duration: 1, ease: "easeInOut" }}
                                            stroke="white"
                                        />
                                    </motion.svg>

                                    <motion.div
                                        className="absolute w-32 h-32 -top-4 -left-4"
                                        initial={{ opacity: 0, scale: 0.5 }}
                                        animate={{ opacity: [0, 1, 0], scale: [0.5, 1.2, 0.5] }}
                                        transition={{
                                            duration: 1.5,
                                            repeat: Infinity,
                                            repeatDelay: 2,
                                        }}
                                    >
                                        <div className="absolute w-2 h-2 bg-yellow-400 rounded-full top-4 left-10 blur-[1px]" />
                                        <div className="absolute w-3 h-3 bg-emerald-300 rounded-full top-10 left-20 blur-[1px]" />
                                        <div className="absolute w-2 h-2 bg-white rounded-full top-8 left-6 blur-[1px]" />
                                    </motion.div>
                                </>
                            ) : (
                                <motion.svg
                                    key="cross"
                                    className="w-14 h-14 text-white"
                                    viewBox="0 0 52 52"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <motion.line
                                        x1="16"
                                        y1="16"
                                        x2="36"
                                        y2="36"
                                        initial={{ pathLength: 0 }}
                                        animate={{ pathLength: 1 }}
                                        transition={{ duration: 0.6 }}
                                        stroke="white"
                                    />
                                    <motion.line
                                        x1="36"
                                        y1="16"
                                        x2="16"
                                        y2="36"
                                        initial={{ pathLength: 0 }}
                                        animate={{ pathLength: 1 }}
                                        transition={{ duration: 0.6, delay: 0.2 }}
                                        stroke="white"
                                    />
                                </motion.svg>
                            )}
                        </AnimatePresence>
                    </div>
                </motion.div>

                <motion.h1
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="text-3xl font-bold text-center text-gray-800"
                >
                    {isSuccess ? "Payment Successful!" : "Payment Failed"}
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="text-gray-500 text-center mt-3 mb-8 leading-relaxed"
                >
                    {isSuccess
                        ? "Your payment has been processed successfully. You’ll receive a confirmation email shortly."
                        : "We couldn’t complete your payment. Please verify your details or try another method."}
                </motion.p>

                <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className={`w-full py-3 rounded-xl font-semibold text-white text-lg transition-all shadow-md ${isSuccess
                        ? "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                        : "bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700"
                        }`}
                >
                    {isSuccess ? "Go to Dashboard" : "Try Again"}
                </motion.button>
            </motion.div>
        </div>
    );
}

export default SuccessPagePayment;